import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import HomePage from './components/Homepage';
import TestSelectionPage from './components/TestSelectionPage';
import StatGenieReportCard from './components/StatGenieReportCard';
import StatGenieReportCardV2 from './components/StatGenieReportCardV2';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Wrapper component to use useNavigate
const AppContent = ({ 
  currentStudent, setCurrentStudent, selectedTest, setSelectedTest,
  studentTests, setStudentTests,
  questionsData, setQuestionsData, students, setStudents, tests, setTests,
  handleDataUpload, handleClearData, isLoading,
  jwtToken, setJwtToken, studentCategory, setStudentCategory
}) => {
  const navigate = useNavigate();

  // Handle student login success
  const handleStudentLogin = (studentData) => {
    setCurrentStudent(studentData);
    setStudentTests(studentData.tests || []);
    localStorage.setItem('currentStudent', JSON.stringify(studentData));
    localStorage.setItem('studentTests', JSON.stringify(studentData.tests || []));
    navigate('/tests');
  };

  // Handle test selection from TestSelectionPage
  const handleTestSelect = (testId, category) => {
    setSelectedTest({ testId, category });
    localStorage.setItem('selectedTest', JSON.stringify({ testId, category }));
    // Category lives in MongoDB only — no localStorage duplication
    if (category) {
      setStudentCategory(category);
    }
    navigate('/report-v2');
  };

  // Handle logout
  const handleLogout = () => {
    setCurrentStudent(null);
    setSelectedTest(null);
    setStudentTests([]);
    setJwtToken(null);
    setStudentCategory(null);
    localStorage.removeItem('currentStudent');
    localStorage.removeItem('selectedTest');
    localStorage.removeItem('studentTests');
    localStorage.removeItem('jwtToken');
    // Category is NOT removed from localStorage because it's not stored there anymore
    navigate('/');
  };

  // Handle back from report
  const handleBackFromReport = () => {
    setSelectedTest(null);
    localStorage.removeItem('selectedTest');
    navigate('/tests');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-100">
        <div className="text-xl font-semibold text-slate-700">Loading application...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          currentStudent ? (
            selectedTest ? (
              <Navigate to="/report-v2" replace />
            ) : (
              <Navigate to="/tests" replace />
            )
          ) : (
            <HomePage
              onDataUpload={handleDataUpload}
              onClearData={handleClearData}
              hasData={questionsData.length > 0}
              questionsData={questionsData}
              students={students}
              tests={tests}
              onStudentLogin={handleStudentLogin}
              jwtToken={jwtToken}
            />
          )
        }
      />
      
      <Route
        path="/tests"
        element={
          currentStudent ? (
            <TestSelectionPage
              studentPin={currentStudent.imsPin}
              studentName={currentStudent.name || currentStudent.imsPin}
              category={studentCategory}
              tests={studentTests}
              onSelectTest={handleTestSelect}
              onLogout={handleLogout}
              jwtToken={jwtToken}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      
      <Route
        path="/report"
        element={
          currentStudent && selectedTest ? (
            <StatGenieReportCard
              selectedStudent={`student_${currentStudent.imsPin}`}
              selectedTest={selectedTest.testId}
              studentCategory={selectedTest.category || currentStudent.category}
              onBack={handleBackFromReport}
              jwtToken={jwtToken}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      
      <Route
        path="/report-v2"
        element={
          currentStudent && selectedTest ? (
            <StatGenieReportCardV2
              studentPin={currentStudent.imsPin}
              studentName={currentStudent?.name || currentStudent?.imsPin}
              testId={selectedTest.testId}
              category={selectedTest.category}
              token={jwtToken}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
    </Routes>
  );
};

const App = () => {
  // Centralized state for all data
  const [questionsData, setQuestionsData] = useState([]);
  const [students, setStudents] = useState([]);
  const [tests, setTests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Student session state
  const [currentStudent, setCurrentStudent] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [studentTests, setStudentTests] = useState([]);

  // JWT token — populated from URL ?token= or localStorage
  const [jwtToken, setJwtToken] = useState(null);

  // Student category — fetched once in App.js, passed as prop to children
  const [studentCategory, setStudentCategory] = useState(null);

  // Load data from localStorage on initial render + detect JWT in URL
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        // ── Check URL params for JWT token ──
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token');
        // Support both camelCase (testId/assetId) and snake_case (test_id/asset_id)
        const urlTestId = params.get('testId') || params.get('test_id');
        const urlAssetId = params.get('assetId') || params.get('asset_id');

        // Priority: URL token > localStorage token
        const savedToken = localStorage.getItem('jwtToken');
        const activeToken = urlToken || savedToken;

        if (activeToken) {
          console.log('🔑 JWT token detected — initiating API-first login flow');
          setJwtToken(activeToken);
          localStorage.setItem('jwtToken', activeToken);

          // Auto-fetch profile and tests from API
          try {
            const profileRes = await fetch(`${API_BASE_URL}/api/student/profile`, {
              headers: { Authorization: `Bearer ${activeToken}` }
            });
            const profileData = await profileRes.json();

            const testsRes = await fetch(`${API_BASE_URL}/api/student/tests-api`, {
              headers: { Authorization: `Bearer ${activeToken}` }
            });
            const testsData = await testsRes.json();

            if (profileData.success && testsData.success) {
              const profile = profileData.data;
              const apiTests = testsData.data;

              // ── Check mvp-ai-insights DB for stored category ──
              let resolvedCategory = null;
              try {
                const categoryRes = await fetch(`${API_BASE_URL}/api/student/${profile.studentPin}/category`, {
                  headers: { Authorization: `Bearer ${activeToken}` }
                });
                const categoryData = await categoryRes.json();
                if (categoryData.success && categoryData.data?.category) {
                  resolvedCategory = categoryData.data.category;
                  setStudentCategory(resolvedCategory);
                  console.log(`📂 Category from DB: ${resolvedCategory}`);
                }
              } catch (catErr) {
                console.warn('⚠️ Could not fetch stored category:', catErr.message);
              }

              // If no stored category, continue to tests page - category modal will show on AI Insights click
              if (!resolvedCategory) {
                console.log('⚠️ No stored category found - will prompt on AI Insights click');
              }

              const studentData = {
                imsPin: profile.studentPin,
                name: profile.name,
                tests: apiTests,
                loginMethod: 'jwt'
              };

              setCurrentStudent(studentData);
              setStudentTests(apiTests);
              localStorage.setItem('currentStudent', JSON.stringify(studentData));
              localStorage.setItem('studentTests', JSON.stringify(apiTests));
              // Category lives in MongoDB only — no localStorage write

              // If a specific testId was passed in URL, pre-select it.
              // Why the fallback below? When the user refreshes on /report-v2
              // after navigating there via handleTestSelect (which calls
              // navigate('/report-v2') with no query string), the URL has no
              // test_id. Without this fallback, the JWT branch below would
              // return early (line: setIsLoading(false); return;) and skip
              // the localStorage selectedTest restore further down — leaving
              // selectedTest === null and bouncing the user back to /tests.
              if (urlTestId) {
                const preSelected = { testId: urlTestId, category: resolvedCategory };
                setSelectedTest(preSelected);
                localStorage.setItem('selectedTest', JSON.stringify(preSelected));
              } else {
                const savedSelectedTest = localStorage.getItem('selectedTest');
                if (savedSelectedTest) {
                  try {
                    const parsed = JSON.parse(savedSelectedTest);
                    // Keep category in sync with whichever source resolved it
                    if (parsed && !parsed.category && resolvedCategory) {
                      parsed.category = resolvedCategory;
                    }
                    setSelectedTest(parsed);
                    console.log(`🔄 Restored selectedTest from localStorage: ${parsed.testId}`);
                  } catch (parseErr) {
                    console.warn('⚠️ Could not parse saved selectedTest:', parseErr.message);
                  }
                }
              }

              // Store resolved category in App state for prop passing
              if (resolvedCategory) {
                setStudentCategory(resolvedCategory);
              }

              console.log(`✅ JWT auto-login complete: ${profile.studentPin} — ${apiTests.length} tests`);
            } else {
              console.warn('⚠️ JWT login: API returned failure, falling through to localStorage');
            }
          } catch (jwtErr) {
            console.warn('⚠️ JWT auto-login failed, falling through to manual login:', jwtErr.message);
          }

          setIsLoading(false);
          return;
        }

        // ── Normal localStorage restore flow ──
        const savedData = localStorage.getItem('questionsData');
        const savedStudents = localStorage.getItem('students');
        const savedTests = localStorage.getItem('tests');
        const savedCurrentStudent = localStorage.getItem('currentStudent');
        const savedSelectedTest = localStorage.getItem('selectedTest');
        const savedStudentTests = localStorage.getItem('studentTests');
        const savedJwt = localStorage.getItem('jwtToken');
        // Category is NOT read from localStorage — MongoDB is the single source of truth

        if (savedData) setQuestionsData(JSON.parse(savedData));
        if (savedStudents) setStudents(JSON.parse(savedStudents));
        if (savedTests) setTests(JSON.parse(savedTests));
        if (savedCurrentStudent) setCurrentStudent(JSON.parse(savedCurrentStudent));
        if (savedSelectedTest) setSelectedTest(JSON.parse(savedSelectedTest));
        if (savedStudentTests) setStudentTests(JSON.parse(savedStudentTests));
        if (savedJwt) setJwtToken(savedJwt);

        // ── Fetch category from MongoDB (not localStorage) ──
        // If we have a student PIN from restored state, fetch their category from DB
        if (savedCurrentStudent) {
          try {
            const parsed = JSON.parse(savedCurrentStudent);
            const pin = parsed.imsPin;
            if (pin) {
              const catRes = await fetch(`${API_BASE_URL}/api/student/${pin}/category`);
              const catData = await catRes.json();
              if (catData.success && catData.data?.category) {
                setStudentCategory(catData.data.category);
                console.log(`📂 Category from DB (restore): ${catData.data.category}`);
              }
            }
          } catch (catErr) {
            console.warn('⚠️ Could not fetch category on restore:', catErr.message);
          }
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
        localStorage.removeItem('questionsData');
        localStorage.removeItem('students');
        localStorage.removeItem('tests');
        localStorage.removeItem('currentStudent');
        localStorage.removeItem('selectedTest');
        localStorage.removeItem('studentTests');
        // studentCategory is not in localStorage, no need to remove
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedData();
  }, []);


  // Function to handle data upload from HomePage
  const handleDataUpload = (data, studentCols, testNames) => {
    try {
      // Update state
      setQuestionsData(data);
      setStudents(studentCols);
      setTests(testNames);

      // Save to localStorage
      localStorage.setItem('questionsData', JSON.stringify(data));
      localStorage.setItem('students', JSON.stringify(studentCols));
      localStorage.setItem('tests', JSON.stringify(testNames));
    } catch (error) {
      console.error('Error saving data:', error);
      // Potentially alert the user or handle the error more gracefully
    }
  };

  // Function to clear all data
  const handleClearData = () => {
    setQuestionsData([]);
    setStudents([]);
    setTests([]);
    localStorage.removeItem('questionsData');
    localStorage.removeItem('students');
    localStorage.removeItem('tests');
    localStorage.removeItem('fileName');
  };

  return (
    <Router>
      <AppContent
        currentStudent={currentStudent}
        setCurrentStudent={setCurrentStudent}
        selectedTest={selectedTest}
        setSelectedTest={setSelectedTest}
        studentTests={studentTests}
        setStudentTests={setStudentTests}
        questionsData={questionsData}
        setQuestionsData={setQuestionsData}
        students={students}
        setStudents={setStudents}
        tests={tests}
        setTests={setTests}
        handleDataUpload={handleDataUpload}
        handleClearData={handleClearData}
        isLoading={isLoading}
        jwtToken={jwtToken}
        setJwtToken={setJwtToken}
        studentCategory={studentCategory}
        setStudentCategory={setStudentCategory}
      />
    </Router>
  );
};

export default App;
